import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, forkJoin, map, mergeMap, of, switchMap } from 'rxjs';

import { PartnerManagementService } from '../services/partner-management.service';
import { getApiErrorMessage } from '../../../core/utils/api-error.util';
import { PartnerManagementActions } from './partner-management.actions';

@Injectable()
export class PartnerManagementEffects {
  private readonly actions$ = inject(Actions);
  private readonly partnerService = inject(PartnerManagementService);

  readonly loadList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PartnerManagementActions.loadList),
      switchMap(({ query }) =>
        forkJoin({
          list: this.partnerService.listPartners(query),
          counts: this.partnerService.getDashboardCounts(),
        }).pipe(
          map(({ list, counts }) =>
            PartnerManagementActions.loadListSuccess({
              rows: list.items,
              total: list.total,
              page: list.page,
              pageSize: list.limit,
              counts,
            }),
          ),
          catchError((error: unknown) =>
            of(
              PartnerManagementActions.loadListFailure({
                error: getApiErrorMessage(error, 'Unable to load partners'),
              }),
            ),
          ),
        ),
      ),
    ),
  );

  readonly loadPartner$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PartnerManagementActions.loadPartner),
      switchMap(({ partnerId }) =>
        this.partnerService.getPartner(partnerId).pipe(
          map((partner) => PartnerManagementActions.loadPartnerSuccess({ partner })),
          catchError((error: unknown) =>
            of(
              PartnerManagementActions.loadPartnerFailure({
                error: getApiErrorMessage(error, 'Unable to load partner details'),
              }),
            ),
          ),
        ),
      ),
    ),
  );

  readonly createPartner$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PartnerManagementActions.createPartner),
      switchMap(({ payload }) =>
        this.partnerService.createPartner(payload).pipe(
          map((response) =>
            PartnerManagementActions.persistSuccess({
              message: response.message,
              partner: response.partner,
            }),
          ),
          catchError((error: unknown) =>
            of(
              PartnerManagementActions.persistFailure({
                error: getApiErrorMessage(error, 'Unable to create partner'),
              }),
            ),
          ),
        ),
      ),
    ),
  );

  readonly updatePartner$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PartnerManagementActions.updatePartner),
      switchMap(({ partnerId, payload }) =>
        this.partnerService.updatePartner(partnerId, payload).pipe(
          map((response) =>
            PartnerManagementActions.persistSuccess({
              message: response.message,
              partner: response.partner,
            }),
          ),
          catchError((error: unknown) =>
            of(
              PartnerManagementActions.persistFailure({
                error: getApiErrorMessage(error, 'Unable to update partner'),
              }),
            ),
          ),
        ),
      ),
    ),
  );

  readonly updatePartnerStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PartnerManagementActions.updatePartnerStatus),
      switchMap(({ partnerId, status }) =>
        this.partnerService.updatePartnerStatus(partnerId, status).pipe(
          map((response) =>
            PartnerManagementActions.updatePartnerStatusSuccess({
              message: response.message,
              partner: response.partner,
            }),
          ),
          catchError((error: unknown) =>
            of(
              PartnerManagementActions.updatePartnerStatusFailure({
                error: getApiErrorMessage(error, 'Unable to update partner status'),
              }),
            ),
          ),
        ),
      ),
    ),
  );

  readonly loadSchedule$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PartnerManagementActions.loadSchedule),
      switchMap(({ partnerId, query }) =>
        this.partnerService.getPartnerSchedule(partnerId, query).pipe(
          map((schedule) =>
            PartnerManagementActions.loadScheduleSuccess({
              partnerId,
              query,
              schedule,
            }),
          ),
          catchError((error: unknown) =>
            of(
              PartnerManagementActions.loadScheduleFailure({
                error: getApiErrorMessage(error, 'Unable to load partner schedule'),
              }),
            ),
          ),
        ),
      ),
    ),
  );

  readonly updateScheduleAvailability$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PartnerManagementActions.updateScheduleAvailability),
      switchMap(({ partnerId, payload, query }) =>
        this.partnerService.updatePartnerDayAvailability(partnerId, payload).pipe(
          mergeMap((response) => [
            PartnerManagementActions.updateScheduleAvailabilitySuccess({
              response,
            }),
            PartnerManagementActions.loadSchedule({ partnerId, query }),
          ]),
          catchError((error: unknown) =>
            of(
              PartnerManagementActions.updateScheduleAvailabilityFailure({
                error: getApiErrorMessage(error, 'Unable to update partner availability'),
              }),
            ),
          ),
        ),
      ),
    ),
  );
}
